import { map } from 'rxjs/operators';
import { getApiUrl, API_URL } from '@/utils/api';
import { normalizeUrl } from '@/utils/menuUtils';
import { valueof } from '@/types';

import ApiBase from './ApiBase';
import { MENU } from '@/constants/menu';
import { PAGES } from '@/constants/pages';
import { SEO_TYPES } from '@/constants/seo';

type SeoPageOptions = {
  categType?: boolean;
  item?: boolean;
}

export default class SeoService extends ApiBase {
  static baseUrl = getApiUrl(API_URL.PRODUCT, '/seo');
  static blogBaseUrl = getApiUrl(API_URL.PRODUCT, '/blog');

  getInfo(storeId: string, url: string, isSpirit = false) {
    const encodedUrl = encodeURIComponent(url);

    const apiUrl = `${SeoService.baseUrl}/${isSpirit ? 'getSpiritInfo' : 'getInfo'}?storeId=${storeId}&url=${encodedUrl}`;

    return this.api.getJSON(apiUrl).pipe(
      map((response) => {
        let redirect;

        const params = {
          storeId,
          url,
          ...response,
          id: response._id
        };

        delete params._id;

        const { page, options } = this.getPageType(response?.type);

        if (page === SEO_TYPES.REDIRECT || page === SEO_TYPES.REDIRECT_PERMANENT) {
          redirect = {};
          redirect.url = normalizeUrl(response.url);
          redirect.permanent = page === SEO_TYPES.REDIRECT_PERMANENT;
        } else if (options.item) {
          params.id = `${response.parent}-${storeId}`;
          params.item = response._id;
        } else if (options.categType) {
          params.categType = response.categType;
        }

        return { page, params, redirect };
      })
    );
  }

  getPageType(type: string): { page?: valueof<typeof PAGES>; options: SeoPageOptions } {
    const options: SeoPageOptions = {};
    let page: valueof<typeof PAGES>;

    if (type) {
      switch (type) {
        case SEO_TYPES.PRODUCT:
        case MENU.PRODUCT:
          page = PAGES.PRODUCT;
          break;
        case SEO_TYPES.CATEG:
        case MENU.CATEG:
          page = PAGES.CATEG;
          break;
        case SEO_TYPES.CATEG_WB:
          page = PAGES.CATEG_WB;
          break;
        case SEO_TYPES.CATEG_CU:
          page = PAGES.CATEG_CU;
          break;
        case SEO_TYPES.REDIRECT_302:
        case SEO_TYPES.REDIRECT:
          page = SEO_TYPES.REDIRECT;
          break;
        case SEO_TYPES.REDIRECT_PERMANENT:
          page = SEO_TYPES.REDIRECT_PERMANENT;
          break;
        case SEO_TYPES.ESPOT:
        case SEO_TYPES.HTMLCONTENT:
        case MENU.LANDING_PAGE:
          page = PAGES.LANDING_PAGE;
          break;
        case SEO_TYPES.ITEM:
          page = PAGES.PRODUCT;
          options.item = true;
          break;
        case SEO_TYPES.CATEGLISTING:
          page = PAGES.CATEG_LISTING;
          options.categType = true;
          break;
        case SEO_TYPES.CATEG_SPIRIT:
          page = PAGES.CATEG_SPIRIT;
          break;
        case SEO_TYPES.PRODUCT_SPIRIT:
          page = PAGES.PRODUCT_SPIRIT;
          break;
        case SEO_TYPES.ITEM_SPIRIT:
          page = PAGES.PRODUCT_SPIRIT;
          options.item = true;
          break;
        case SEO_TYPES.CUSTOMER_LOYALTY:
          page = PAGES.CUSTOMER_LOYALTY;
          break;
        default:
          console.warn('SeoService.getPageType: invalid page type', type);
          break;
      }
    }

    return { page, options };
  }

  getBlogData({ storeId, url, isArchivePage, page = 1, tagFilter = '' }) {
    let blogBaseUrl = `${SeoService.baseUrl}/getBlogInfo?url=${encodeURIComponent(url)}&`;

    if (isArchivePage) {
      blogBaseUrl = `${SeoService.blogBaseUrl}/articles/archives/${encodeURIComponent(url)}?`;
    }

    return this.api.getJSON(`${blogBaseUrl}storeId=${storeId}&page=${page}&tagFilter=${tagFilter}`);
  }

  getFilteredCategBlogData({ storeId, articleCategId, topArticleAggregationId = '', page = 1, tagFilter }) {
    return this.api.getJSON(`${SeoService.blogBaseUrl}/articles/ByCategAndTag/paginate?storeId=${storeId}&articleCategId=${articleCategId}&topArticleAggregationId=${topArticleAggregationId}&page=${page}&tagFilter=${tagFilter}`);
  }

  getFilteredArchiveBlogData(storeId: string, slug: string, page = 1) {
    return this.api.getJSON(`${SeoService.blogBaseUrl}/articles/archives/${slug}/paginate?storeId=${storeId}&page=${page}`);
  }

  getFilteredTagBlogData(storeId: string, tagId: string, page = 1) {
    return this.api.getJSON(`${SeoService.blogBaseUrl}/articles/ByTag/paginate?storeId=${storeId}&page=${page}&tagId=${tagId}`);
  }

  getFilteredArchiveAndTagBlogData(storeId: string, queryParam, page = 1, isArchivePage = false) {
    return isArchivePage ?
      this.getFilteredArchiveBlogData(storeId, queryParam, page) :
      this.getFilteredTagBlogData(storeId, queryParam, page);
  }
}
